import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';

export type WindowType = 
  | 'invoice' 
  | 'customer' 
  | 'vendor' 
  | 'product' 
  | 'bulk-upload-products'
  | 'bulk-upload-customers'
  | 'bulk-upload-vendors'
  | 'ledger-entry' 
  | 'return-invoice'
  | 'category'
  | 'customer-group'
  | 'vendor-group';

export interface WindowProgress {
  isLoading: boolean;
  progressPercent?: number;
  progressLabel?: string;
}

export interface ManagedWindow {
  id: string;
  type: WindowType;
  title: string;
  isMinimized: boolean;
  props: Record<string, any>;
  formData?: Record<string, any>;
  createdAt: Date;
  zIndex: number;
  progress?: WindowProgress;
}

interface WindowManagerContextType {
  windows: ManagedWindow[];
  openWindow: (type: WindowType, props?: Record<string, any>, title?: string, onRestore?: () => void) => string;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  updateWindowData: (id: string, formData: Record<string, any>) => void;
  updateWindowTitle: (id: string, title: string) => void;
  updateWindowProgress: (id: string, progress: WindowProgress) => void;
  getWindow: (id: string) => ManagedWindow | undefined;
  bringToFront: (id: string) => void;
  isWindowOpen: (type: WindowType, props?: Record<string, any>) => boolean;
  registerRestoreCallback: (id: string, callback: () => void) => void;
}

const WindowManagerContext = createContext<WindowManagerContextType | undefined>(undefined);

let windowIdCounter = 0;
let zIndexCounter = 100;

export const WindowManagerProvider = ({ children }: { children: ReactNode }) => {
  const [windows, setWindows] = useState<ManagedWindow[]>([]);
  const restoreCallbacksRef = useRef<Map<string, () => void>>(new Map());

  const openWindow = useCallback((
    type: WindowType, 
    props: Record<string, any> = {}, 
    title?: string,
    onRestore?: () => void
  ): string => {
    const id = `window-${type}-${++windowIdCounter}`;
    const defaultTitles: Record<WindowType, string> = {
      'invoice': 'New Invoice',
      'customer': 'New Customer',
      'vendor': 'New Vendor',
      'product': 'New Product',
      'bulk-upload-products': 'Bulk Upload Products',
      'bulk-upload-customers': 'Bulk Upload Customers',
      'bulk-upload-vendors': 'Bulk Upload Vendors',
      'ledger-entry': 'Ledger Entry',
      'return-invoice': 'Return Invoice',
      'category': 'Category',
      'customer-group': 'Customer Group',
      'vendor-group': 'Vendor Group',
    };

    const newWindow: ManagedWindow = {
      id,
      type,
      title: title || defaultTitles[type] || 'Window',
      isMinimized: false,
      props,
      formData: {},
      createdAt: new Date(),
      zIndex: ++zIndexCounter,
      progress: undefined,
    };

    // Store restore callback if provided
    if (onRestore) {
      restoreCallbacksRef.current.set(id, onRestore);
    }

    setWindows(prev => [...prev, newWindow]);
    return id;
  }, []);

  const closeWindow = useCallback((id: string) => {
    restoreCallbacksRef.current.delete(id);
    setWindows(prev => prev.filter(w => w.id !== id));
  }, []);

  const minimizeWindow = useCallback((id: string) => {
    setWindows(prev => prev.map(w => 
      w.id === id ? { ...w, isMinimized: true } : w
    ));
  }, []);

  const restoreWindow = useCallback((id: string) => {
    // Call the restore callback to reopen the dialog
    const callback = restoreCallbacksRef.current.get(id);
    if (callback) {
      callback();
    }
    
    setWindows(prev => prev.map(w => 
      w.id === id ? { ...w, isMinimized: false, zIndex: ++zIndexCounter } : w
    ));
  }, []);

  const registerRestoreCallback = useCallback((id: string, callback: () => void) => {
    restoreCallbacksRef.current.set(id, callback);
  }, []);

  const updateWindowData = useCallback((id: string, formData: Record<string, any>) => {
    setWindows(prev => prev.map(w => 
      w.id === id ? { ...w, formData: { ...w.formData, ...formData } } : w
    ));
  }, []);

  const updateWindowTitle = useCallback((id: string, title: string) => {
    setWindows(prev => prev.map(w => 
      w.id === id ? { ...w, title } : w
    ));
  }, []);

  const updateWindowProgress = useCallback((id: string, progress: WindowProgress) => {
    setWindows(prev => prev.map(w => 
      w.id === id ? { ...w, progress } : w
    ));
  }, []);

  const getWindow = useCallback((id: string) => {
    return windows.find(w => w.id === id);
  }, [windows]);

  const bringToFront = useCallback((id: string) => {
    setWindows(prev => prev.map(w => 
      w.id === id ? { ...w, zIndex: ++zIndexCounter, isMinimized: false } : w
    ));
  }, []);

  const isWindowOpen = useCallback((type: WindowType, props?: Record<string, any>) => {
    return windows.some(w => {
      if (w.type !== type) return false;
      if (!props) return true;
      return Object.keys(props).every(key => w.props[key] === props[key]);
    });
  }, [windows]);

  return (
    <WindowManagerContext.Provider value={{
      windows,
      openWindow,
      closeWindow,
      minimizeWindow,
      restoreWindow,
      updateWindowData,
      updateWindowTitle,
      updateWindowProgress,
      getWindow,
      bringToFront,
      isWindowOpen,
      registerRestoreCallback,
    }}>
      {children}
    </WindowManagerContext.Provider>
  );
};

export const useWindowManager = () => {
  const context = useContext(WindowManagerContext);
  if (!context) {
    throw new Error('useWindowManager must be used within a WindowManagerProvider');
  }
  return context;
};
